import { useState } from 'react';
import { Building2, Users, DollarSign, Globe, Briefcase, TrendingUp, MapPin, Crown, Swords, Calendar, Landmark, ChartBar as BarChart3, Database, Zap, Target, ChevronDown, ChevronUp } from 'lucide-react';
import type { Account } from '../../../types';
import FormattedText from '../../common/FormattedText';

interface Props {
  account: Account;
}

function MetricBox({ icon: Icon, label, value, accent }: { icon: typeof Building2; label: string; value: string; accent?: boolean }) {
  if (!value) return null;
  return (
    <div className={`p-4 rounded-lg ${accent ? 'bg-forest-50 border border-forest-100' : 'bg-app-canvas'}`}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className={`w-3.5 h-3.5 ${accent ? 'text-forest-500' : 'text-app-muted'}`} />
        <p className={`text-xs font-semibold uppercase tracking-wider ${accent ? 'text-forest-600' : 'text-app-muted'}`}>{label}</p>
      </div>
      <p className={`text-sm font-medium ${accent ? 'text-forest-800' : 'text-app-ink'}`}>{value}</p>
    </div>
  );
}

function formatTamValue(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

function TAMEstimateCard({ account }: { account: Account }) {
  const [expanded, setExpanded] = useState(false);
  const hasTam = account.overview_tam_max > 0 && account.enrichment_status === 'completed';
  if (!hasTam) return null;

  const assumptions = Array.isArray(account.overview_tam_assumptions) ? account.overview_tam_assumptions : [];
  const confidenceColors = {
    high: 'bg-app-positive/10 text-app-positive border-app-positive',
    medium: 'bg-app-warning/10 text-app-warning border-app-warning',
    low: 'bg-app-canvas text-app-muted border-app-line',
  };
  const conf = account.overview_tam_confidence || 'low';

  return (
    <div className="p-4 bg-app-focus/10 rounded-lg border border-app-focus/30">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Target className="w-3.5 h-3.5 text-app-focus" />
          <p className="text-xs font-semibold text-app-focus uppercase tracking-wider">Addressable Opportunity</p>
        </div>
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${confidenceColors[conf]}`}>
          {conf} confidence
        </span>
      </div>
      <p className="text-lg font-bold text-app-focus mb-1.5">
        {formatTamValue(account.overview_tam_min)} to {formatTamValue(account.overview_tam_max)}
        <span className="text-xs font-medium text-app-focus ml-1.5">annually</span>
      </p>
      {account.overview_tam_summary && (
        <p className="text-xs text-app-focus leading-relaxed mb-2">{account.overview_tam_summary}</p>
      )}
      {assumptions.length > 0 && (
        <div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-app-focus hover:text-app-focus transition-colors"
          >
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {expanded ? 'Hide' : 'View'} assumptions
          </button>
          {expanded && (
            <ul className="mt-2 space-y-1">
              {assumptions.map((a, i) => (
                <li key={i} className="text-[11px] text-app-focus flex items-start gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-app-focus mt-1.5 flex-shrink-0" />
                  {a}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <p className="text-[10px] text-app-focus mt-2 italic">Directional estimate based on company profile data. Not a quote.</p>
    </div>
  );
}

export default function CompanyProfileCard({ account }: Props) {
  const hasData = account.enrichment_status === 'completed' || account.company_bio;
  const executives = Array.isArray(account.key_executives) ? account.key_executives : [];
  const competitors = Array.isArray(account.key_competitors) ? account.key_competitors : [];
  const offices = Array.isArray(account.global_office_locations) ? account.global_office_locations : [];

  if (!hasData) {
    return (
      <div className="text-center py-10">
        <Building2 className="w-10 h-10 text-app-line-strong mx-auto mb-3" />
        <p className="text-sm text-app-muted">Click "Populate Account Intelligence" to generate a full company profile</p>
      </div>
    );
  }

  const cacheAge = account.cache_sourced_at
    ? Math.round((Date.now() - new Date(account.cache_sourced_at).getTime()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div className="space-y-5">
      {account.data_source && account.enrichment_status === 'completed' && (
        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
          account.data_source === 'cached'
            ? 'bg-app-focus/10 text-app-focus border border-app-focus/30'
            : 'bg-app-positive/10 text-app-positive border border-app-positive'
        }`}>
          {account.data_source === 'cached' ? (
            <Database className="w-3 h-3" />
          ) : (
            <Zap className="w-3 h-3" />
          )}
          {account.data_source === 'cached'
            ? `Sourced from cache${cacheAge !== null ? ` (${cacheAge}d ago)` : ''}`
            : 'Direct research'}
        </div>
      )}

      {account.company_bio && (
        <div className="p-4 bg-app-canvas rounded-lg">
          <p className="text-xs font-semibold text-app-muted uppercase tracking-wider mb-1.5">Company Bio</p>
          <FormattedText text={account.company_bio} className="text-sm text-app-ink" />
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricBox icon={Users} label="Employees" value={account.employee_count > 0 ? account.employee_count.toLocaleString() : ''} />
        <MetricBox icon={DollarSign} label="Annual Revenue" value={account.revenue || account.annual_recurring_revenue} />
        <MetricBox icon={Globe} label="Company Type" value={account.is_public_company ? 'Public' : 'Private'} />
        <MetricBox icon={Calendar} label="Founded" value={account.founded_year} />
      </div>

      {(account.market_cap || account.total_funding || account.latest_funding_round || account.stock_exchange) && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MetricBox icon={BarChart3} label="Market Cap" value={account.market_cap} />
          <MetricBox icon={TrendingUp} label="Total Funding" value={account.total_funding} />
          <MetricBox icon={TrendingUp} label="Latest Round" value={account.latest_funding_round} />
          <MetricBox icon={Landmark} label="Stock Exchange" value={account.stock_exchange ? `${account.stock_exchange}: ${account.ticker_symbol}` : ''} />
        </div>
      )}

      {account.annual_recurring_revenue && account.revenue && account.annual_recurring_revenue !== account.revenue && (
        <MetricBox icon={DollarSign} label="Annual Recurring Revenue" value={account.annual_recurring_revenue} accent />
      )}

      <TAMEstimateCard account={account} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {account.hq_location && (
          <div className="p-4 bg-app-canvas rounded-lg">
            <div className="flex items-center gap-1.5 mb-2">
              <MapPin className="w-3.5 h-3.5 text-app-muted" />
              <p className="text-xs font-semibold text-app-muted uppercase tracking-wider">Headquarters</p>
            </div>
            <p className="text-sm font-medium text-app-ink">{account.hq_location}</p>
          </div>
        )}
        {offices.length > 0 && (
          <div className="p-4 bg-app-canvas rounded-lg">
            <div className="flex items-center gap-1.5 mb-2">
              <Globe className="w-3.5 h-3.5 text-app-muted" />
              <p className="text-xs font-semibold text-app-muted uppercase tracking-wider">Global Offices</p>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {offices.map((loc, i) => (
                <span key={i} className="inline-flex items-center px-2.5 py-1 bg-app-surface border border-app-line rounded-md text-xs text-app-ink">{loc}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {account.how_they_make_money && (
        <div className="p-4 bg-forest-50 rounded-lg border border-forest-100">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Briefcase className="w-3.5 h-3.5 text-forest-600" />
            <p className="text-xs font-semibold text-forest-600 uppercase tracking-wider">How They Make Money</p>
          </div>
          <p className="text-sm text-forest-800 leading-relaxed">{account.how_they_make_money}</p>
        </div>
      )}

      {account.business_model && (
        <MetricBox icon={Briefcase} label="Business Model" value={account.business_model} />
      )}

      {account.number_of_customers && (
        <MetricBox icon={Users} label="Customer Base" value={account.number_of_customers} />
      )}

      {executives.length > 0 && (
        <div className="p-4 bg-app-canvas rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-1.5">
              <Crown className="w-3.5 h-3.5 text-app-muted" />
              <p className="text-xs font-semibold text-app-muted uppercase tracking-wider">Key Executives</p>
            </div>
            <span className="text-[10px] text-app-muted">
              Import to Org Chart via Execute tab
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {executives.map((exec, i) => (
              <div key={i} className="px-3 py-2 bg-app-surface border border-app-line rounded-lg">
                <p className="text-sm font-medium text-app-ink truncate">{exec.name}</p>
                <p className="text-xs text-app-muted truncate">{exec.title}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {competitors.length > 0 && (
        <div className="p-4 bg-app-canvas rounded-lg">
          <div className="flex items-center gap-1.5 mb-2">
            <Swords className="w-3.5 h-3.5 text-app-muted" />
            <p className="text-xs font-semibold text-app-muted uppercase tracking-wider">Key Competitors</p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {competitors.map((name, i) => (
              <span key={i} className="inline-flex items-center px-2.5 py-1 bg-app-surface border border-app-line rounded-md text-xs font-medium text-app-ink">{name}</span>
            ))}
          </div>
        </div>
      )}

      {account.recent_acquisitions && (
        <MetricBox icon={TrendingUp} label="Recent Acquisitions" value={account.recent_acquisitions} />
      )}
    </div>
  );
}
